import {
    ApiOutlined,
    RobotOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons'

interface InfoItem {
    title: string
    text: string
    icon: React.ComponentType<any>
}

interface Translations {
    [key: string]: {
        title: string
        description: string
        button: string
        info: InfoItem[]
    }
}

/**
 * WhatIs — первая объясняющая секция после Banner+TrustSignals.
 * Должна за 10 секунд донести: "что это", "как это работает", "почему
 * это безопасно".  Три карточки по основным столпам:
 *   1. Алгоритм (что торгует)
 *   2. Биржи (где торгует)
 *   3. Безопасность (как защищены деньги)
 */
export const translations: Translations = {
    en: {
        title: 'Automated trading, engineered for real results',
        description:
            'RealColibri is a turnkey trading automation platform — connect your exchange, choose a symbol, and the algorithm trades 24/7 while you sleep. Built on 20+ years of futures experience.',
        button: 'Learn more',
        info: [
            {
                title: 'Battle-tested algorithm',
                text: 'Proprietary high-frequency strategy with thousands of trades per day per account. Designed to be steady in calm markets and adaptive in volatility.',
                icon: RobotOutlined,
            },
            {
                title: 'Four exchanges, one platform',
                text: 'Binance, Bybit, OKX for crypto futures plus Rithmic for global futures (CME, EUREX, SGX). Pick the market you trust — we handle the rest.',
                icon: ApiOutlined,
            },
            {
                title: 'Your funds stay on your exchange',
                text: 'We trade with Read & Trade API only — withdraw permission is never required. Your capital never leaves your custody.',
                icon: SafetyCertificateOutlined,
            },
        ],
    },
    ru: {
        title: 'Автоматическая торговля для реальных результатов',
        description:
            'RealColibri — это готовая платформа автоматизации торговли. Подключите биржу, выберите символ — и алгоритм торгует 24/7, пока вы спите. Построен на 20+ годах опыта на фьючерсах.',
        button: 'Узнать больше',
        info: [
            {
                title: 'Проверенный алгоритм',
                text: 'Собственная высокочастотная стратегия с тысячами сделок в день. Стабильна в спокойном рынке, адаптивна в волатильности.',
                icon: RobotOutlined,
            },
            {
                title: 'Четыре биржи, одна платформа',
                text: 'Binance, Bybit, OKX для крипто-фьючерсов плюс Rithmic для глобальных фьючерсов (CME, EUREX, SGX). Выбирайте рынок, которому доверяете — остальное на нас.',
                icon: ApiOutlined,
            },
            {
                title: 'Ваши средства остаются на бирже',
                text: 'Мы торгуем только по Read & Trade API — право вывода никогда не требуется. Деньги всё время лежат на вашем счёте, и распоряжаетесь ими только вы.',
                icon: SafetyCertificateOutlined,
            },
        ],
    },
    es: {
        title: 'Trading automatizado, diseñado para resultados reales',
        description:
            'RealColibri es una plataforma de trading automatizado llave en mano. Conecta tu exchange, elige un símbolo, y el algoritmo opera 24/7 mientras duermes. Basado en 20+ años de experiencia en futuros.',
        button: 'Aprender más',
        info: [
            {
                title: 'Algoritmo probado en combate',
                text: 'Estrategia propia de alta frecuencia con miles de operaciones diarias por cuenta. Estable en mercados tranquilos, adaptativa en volatilidad.',
                icon: RobotOutlined,
            },
            {
                title: 'Cuatro exchanges, una plataforma',
                text: 'Binance, Bybit, OKX para crypto futures más Rithmic para futuros globales (CME, EUREX, SGX). Elige el mercado que prefieras — del resto nos ocupamos nosotros.',
                icon: ApiOutlined,
            },
            {
                title: 'Tus fondos permanecen en tu exchange',
                text: 'Operamos solo con API Read & Trade — el permiso de retiro nunca es necesario. Tu capital nunca sale de tu custodia.',
                icon: SafetyCertificateOutlined,
            },
        ],
    },
    hy: {
        title: 'Ավտոմատ առևտուր՝ իրական արդյունքների համար',
        description:
            'RealColibri-ն առևտրի ավտոմատացման պատրաստ հարթակ է: Միացրեք բորսան, ընտրեք սիմվոլը — և ալգորիթմը առևտուր է անում 24/7, մինչ դուք քնում եք: Կառուցված 20+ տարվա ֆյուչերսային փորձի վրա:',
        button: 'Իմանալ ավելին',
        info: [
            {
                title: 'Փորձված ալգորիթմ',
                text: 'Սեփական բարձր հաճախականության ստրատեգիա՝ օրական հազարավոր գործարքներով: Կայուն հանգիստ շուկայում, հարմարվող անկայունության դեպքում:',
                icon: RobotOutlined,
            },
            {
                title: 'Չորս բորսա, մեկ հարթակ',
                text: 'Binance, Bybit, OKX կրիպտո ֆյուչերսների համար, գումարած Rithmic գլոբալ ֆյուչերսների համար (CME, EUREX, SGX): Ընտրեք շուկան, որին վստահում եք — մնացածը մերն է:',
                icon: ApiOutlined,
            },
            {
                title: 'Միջոցները մնում են ձեր բորսայում',
                text: 'Առևտուր ենք անում միայն Read & Trade API-ով — դուրսբերման թույլտվությունը երբեք չի պահանջվում: Կապիտալը չի լքում ձեր կուստոդին:',
                icon: SafetyCertificateOutlined,
            },
        ],
    },
    de: {
        title: 'Automatisierter Handel für echte Ergebnisse',
        description:
            'RealColibri ist eine schlüsselfertige Handelsautomatisierungs-Plattform. Verbinden Sie Ihre Börse, wählen Sie ein Symbol — und der Algorithmus handelt 24/7, während Sie schlafen. Aufgebaut auf 20+ Jahren Futures-Erfahrung.',
        button: 'Mehr erfahren',
        info: [
            {
                title: 'Bewährter Algorithmus',
                text: 'Eigene Hochfrequenz-Strategie mit Tausenden Trades pro Tag und Konto. Stabil in ruhigen Märkten, adaptiv bei Volatilität.',
                icon: RobotOutlined,
            },
            {
                title: 'Vier Börsen, eine Plattform',
                text: 'Binance, Bybit, OKX für Krypto-Futures sowie Rithmic für globale Futures (CME, EUREX, SGX). Wählen Sie den Markt Ihres Vertrauens — den Rest übernehmen wir.',
                icon: ApiOutlined,
            },
            {
                title: 'Ihre Mittel bleiben auf Ihrer Börse',
                text: 'Wir handeln nur mit Read & Trade API — Abhebungsberechtigung ist nie erforderlich. Ihr Kapital verlässt nie Ihre Verwahrung.',
                icon: SafetyCertificateOutlined,
            },
        ],
    },
    fr: {
        title: 'Trading automatisé pour des résultats réels',
        description:
            'RealColibri est une plateforme d\'automatisation du trading clé en main. Connectez votre exchange, choisissez un symbole — et l\'algorithme trade 24/7 pendant que vous dormez. Construit sur 20+ ans d\'expérience en futures.',
        button: 'En savoir plus',
        info: [
            {
                title: 'Algorithme éprouvé',
                text: 'Stratégie propriétaire à haute fréquence avec des milliers de trades par jour et par compte. Stable en marché calme, adaptative en volatilité.',
                icon: RobotOutlined,
            },
            {
                title: 'Quatre exchanges, une plateforme',
                text: 'Binance, Bybit, OKX pour les futures crypto plus Rithmic pour les futures mondiaux (CME, EUREX, SGX). Choisissez le marché de confiance — nous nous occupons du reste.',
                icon: ApiOutlined,
            },
            {
                title: 'Vos fonds restent sur votre exchange',
                text: 'Nous tradons uniquement avec une API Read & Trade — le droit de retrait n\'est jamais nécessaire. Votre capital ne quitte jamais votre garde.',
                icon: SafetyCertificateOutlined,
            },
        ],
    },
    it: {
        title: 'Trading automatizzato per risultati veri',
        description:
            'RealColibri è una piattaforma di automazione del trading chiavi in mano. Collega il tuo exchange, scegli un simbolo — e l\'algoritmo opera 24/7 mentre dormi. Costruito su 20+ anni di esperienza sui futures.',
        button: 'Scopri di più',
        info: [
            {
                title: 'Algoritmo testato sul campo',
                text: 'Strategia proprietaria ad alta frequenza con migliaia di trade al giorno per account. Stabile in mercati calmi, adattivo in volatilità.',
                icon: RobotOutlined,
            },
            {
                title: 'Quattro exchange, una piattaforma',
                text: 'Binance, Bybit, OKX per crypto futures più Rithmic per futures globali (CME, EUREX, SGX). Scegli il mercato di cui ti fidi — al resto pensiamo noi.',
                icon: ApiOutlined,
            },
            {
                title: 'I tuoi fondi restano sul tuo exchange',
                text: 'Operiamo solo con API Read & Trade — il permesso di prelievo non è mai richiesto. Il tuo capitale non lascia mai la tua custodia.',
                icon: SafetyCertificateOutlined,
            },
        ],
    },
}
